import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './UsageList.module.scss';
import { formatDate } from '../../utils';
import UsageHoverLabel from '../UsageHoverLabel/UsageHoverLabel';
import { Icon } from '../../../../../components/Icon';

/**
 * @param {CompanyUsage} usage
 * @param {boolean} isSuperAdmin
 * @param {companyUsageCallback} onPencilClick
 * @param {companyUsageCallback} onClick
 * @return {JSX.Element}
 * @constructor
 */
const UsageItem = ({ usage, isSuperAdmin, onPencilClick, onClick }) => {
  const handlePencilClick = useCallback(
    e => {
      e.stopPropagation();
      onPencilClick(usage);
    },
    [onPencilClick, usage]
  );

  const handleUsageClicked = useCallback(() => {
    onClick(usage);
  }, [onClick, usage]);

  const { t } = useTranslation();

  return (
    <div className={styles.usageTableRow} onClick={handleUsageClicked}>
      <div className={styles.usageTableHeaderItem}>{formatDate(usage.startDate)}</div>
      <div className={styles.usageTableHeaderItem}>{formatDate(usage.endDate)}</div>
      <UsageHoverLabel
        text={`${usage.usedMinutes.remaining}`}
        hoverText={t('company.settings.usage.used.hover', { used: usage.usedMinutes.remaining })}
        className={styles.usageTableHeaderItem}
      />
      <UsageHoverLabel
        text={`${usage.usedMinutes.deleted}`}
        hoverText={t('company.settings.usage.deleted.hover', { deleted: usage.usedMinutes.deleted })}
        className={styles.usageTableHeaderItem}
      />
      <UsageHoverLabel
        text={`${usage.usedMinutes.total}\u202F/\u202F${usage.purchasedMinutes}`}
        hoverText={t('company.settings.usage.total_purchased.hover', {
          total: usage.usedMinutes.total,
          purchased: usage.purchasedMinutes,
        })}
        className={styles.usageTableHeaderItem}
      />
      {isSuperAdmin && <Icon className={styles.usagePencil} onClick={handlePencilClick} src="pencil" />}
    </div>
  );
};

/**
 * @param {boolean} isSuperAdmin
 * @return {JSX.Element}
 * @constructor
 */
const UsageHeader = ({ isSuperAdmin }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.usageTableHeader}>
      <div className={styles.usageTableHeaderItem}>{t('company.settings.usage.start_date')}</div>
      <div className={styles.usageTableHeaderItem}>{t('company.settings.usage.end_date')}</div>
      <UsageHoverLabel
        className={styles.usageTableHeaderItem}
        text={t('company.settings.usage.used')}
        hoverText={t('company.settings.usage.used.header.hover')}
      />
      <UsageHoverLabel
        className={styles.usageTableHeaderItem}
        text={t('company.settings.usage.deleted')}
        hoverText={t('company.settings.usage.deleted.header.hover')}
      />
      <UsageHoverLabel
        className={styles.usageTableHeaderItem}
        text={t('company.settings.usage.total_purchased')}
        hoverText={t('company.settings.usage.total_purchased.header.hover')}
      />
      {isSuperAdmin && <Icon className={styles.usageHeaderPencil} src="pencil" />}
    </div>
  );
};

/**
 * @param {CompanyUsage[]} usages
 * @param {boolean} isSuperAdmin
 * @param {companyUsageCallback} handleEdit
 * @param {companyUsageCallback} handleSelectUsage
 * @return {JSX.Element}
 * @constructor
 */
const UsageList = ({ usages, isSuperAdmin, handleEdit, handleSelectUsage }) => {
  const { t } = useTranslation();
  if (!usages.length) {
    return <div className={styles.usageCenterState}>{t('company.settings.usage.empty_state')}</div>;
  }
  return (
    <div>
      <UsageHeader isSuperAdmin={isSuperAdmin} />
      <div className={styles.usageTable}>
        {usages.map(usage => (
          <UsageItem key={usage.id} usage={usage} isSuperAdmin={isSuperAdmin} onPencilClick={handleEdit} onClick={handleSelectUsage} />
        ))}
      </div>
    </div>
  );
};

export default UsageList;
